import { Github, Instagram, Linkedin, ArrowRight, ArrowUp, LockKeyhole } from 'lucide-react'
import { COMPANY } from '../../data/company'
import { NAVIGATION_ITEMS } from '../../utils/constants'
import { scrollToSection } from '../../utils/helpers'

export default function Footer() {
  const year = new Date().getFullYear()
  const socials = [
    { icon: Github, href: COMPANY.social.github, label: 'GitHub' },
    { icon: Linkedin, href: COMPANY.social.linkedin, label: 'LinkedIn' },
    { icon: Instagram, href: COMPANY.social.instagram, label: 'Instagram' },
  ].filter((s) => s.href)

  return (
    <footer className="band !shadow-none">
      <div className="relative mx-auto max-w-6xl xl:max-w-7xl 2xl:max-w-[90rem] px-6 lg:px-10 2xl:px-16 py-16">
        {/* Closing call to action: the last thing on the page is the next step. */}
        <div className="mb-14 flex flex-col gap-6 border-b border-white/10 pb-14 md:flex-row md:items-end md:justify-between">
          <div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-[#48cebc]">Start a project</p>
            <h2 className="mt-3 max-w-2xl text-3xl font-bold leading-[1.1] tracking-[-0.03em] text-white md:text-5xl">
              Have something to build? Let&rsquo;s scope it together.
            </h2>
          </div>
          <a
            href="#contact"
            onClick={(e) => { e.preventDefault(); scrollToSection('contact') }}
            className="inline-flex h-12 shrink-0 items-center justify-center gap-2.5 self-start rounded-full bg-gradient-to-b from-[#0a968a] to-[#037d73] px-7 text-[15px] font-semibold text-white shadow-[inset_0_1px_0_rgb(255_255_255/0.25),0_10px_24px_-10px_rgb(4_150_139/0.7)] transition-[filter,transform] duration-300 hover:-translate-y-px hover:brightness-110 md:self-auto"
          >
            Request a quotation <ArrowRight className="h-4 w-4" />
          </a>
        </div>
        <div className="grid gap-10 md:grid-cols-[1.5fr_1fr_1fr]">
          <div>
            {/* The navy footer always takes the light-on-dark lockup. */}
            <img src="/logo-dark.png" alt="OrionDev" draggable="false" className="h-8 w-auto select-none" />
            <p className="mt-5 max-w-xs text-sm leading-relaxed opacity-75">
              {COMPANY.tagline}.
            </p>
            {socials.length > 0 && <div className="mt-5 flex gap-2">
              {socials.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.label}
                  className="grid h-10 w-10 place-items-center rounded-full border border-white/20 bg-white/5 opacity-80 transition-[opacity,background-color] hover:bg-white/10 hover:opacity-100"
                >
                  <s.icon className="h-[18px] w-[18px]" />
                </a>
              ))}
            </div>}
          </div>

          <div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] opacity-60">Company</p>
            <ul className="mt-4 space-y-2.5">
              {NAVIGATION_ITEMS.map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    onClick={(e) => { e.preventDefault(); scrollToSection(item.href.replace('#', '')) }}
                    className="inline-block text-sm font-medium opacity-80 transition-opacity hover:opacity-100"
                  >
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <p className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] opacity-60">Contact</p>
            <ul className="mt-4 space-y-2.5 text-sm font-medium opacity-80">
              <li><a href={`mailto:${COMPANY.contact.email}`} className="hover:underline">{COMPANY.contact.email}</a></li>
              <li><a href={`tel:${COMPANY.contact.phone}`} className="hover:underline">{COMPANY.contact.phone}</a></li>
              <li>{COMPANY.contact.location}</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="relative border-t border-white/15">
        <div className="mx-auto flex max-w-6xl xl:max-w-7xl 2xl:max-w-[90rem] items-center justify-between px-6 lg:px-10 2xl:px-16 py-6">
          <p className="font-mono text-xs font-medium opacity-60">© {year} OrionDev. All rights reserved.</p>
          <div className="flex items-center gap-2">
            {/* Staff entry to the dashboard (sign-in required). */}
            <a
              href="/admin"
              className="inline-flex h-10 items-center gap-2 rounded-full border border-white/20 bg-white/5 px-4 text-xs font-semibold opacity-80 transition-[opacity,background-color] hover:bg-white/10 hover:opacity-100"
            >
              <LockKeyhole className="h-3.5 w-3.5" /> Admin
            </a>
            <button
              onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
              aria-label="Back to top"
              className="grid h-10 w-10 place-items-center rounded-full border border-white/20 bg-white/5 opacity-80 transition-[opacity,background-color] hover:bg-white/10 hover:opacity-100"
            >
              <ArrowUp className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>
      </div>
    </footer>
  )
}
